import { Api } from '@/api/api';
import { TypesRepsonse } from '@/api/responses/types/types-response';
import { AdditionalDocumentType } from '@/shared/types/additional-document-type';
import { AppointmentCancellationType } from '@/shared/types/appointment-cancellation-type';
import { AppointmentExecutorType } from '@/shared/types/appointment-executor-type';
import { AppointmentNoInstructionType } from '@/shared/types/appointment-no-instruction-type';
import { AppointmentOutcomeType } from '@/shared/types/appointment-outcome-type';
import { AppointmentPaymentStatusType } from '@/shared/types/appointment-payment-status-type';
import { AppointmentProductType } from '@/shared/types/appointment-product-type';
import { AppointmentReasonType } from '@/shared/types/appointment-reason-type';
import { AppointmentType } from '@/shared/types/appointment-type';
import { ClientOriginalSourceType } from '@/shared/types/client-original-source-type';
import { ClientRelationshipType } from '@/shared/types/client-relationship-type';
import { ClientStatusType } from '@/shared/types/client-status-type';
import { ClientTitleType } from '@/shared/types/client-title-type';
import { InteractionLetterType } from '@/shared/types/interaction-letter-type';
import { InteractionOutcomeType } from '@/shared/types/interaction-outcome-type';
import { InteractionType } from '@/shared/types/interaction-type';
import { PartnerPracticeType } from '@/shared/types/partner-practice-type';
import { PowerOfAttorneyType } from '@/shared/types/power-of-attorney-type';
import { WillType } from '@/shared/types/will-type';

export class TypesApi extends Api {
    public async getTypesAsync(): Promise<TypesRepsonse> {
        return await this.getAsync<TypesRepsonse>(`v1/type`);
    }

    public async getAdditionalDocumentTypesAsync(): Promise<Array<AdditionalDocumentType>> {
        return await this.getAsync<Array<AdditionalDocumentType>>(`v1/type/client/additionalDocument`);
    }

    public async createAdditionalDocumentTypeAsync(type: AdditionalDocumentType): Promise<void> {
        await this.postAsync(`v1/type/client/additionalDocument`,
            {
                value: type.value,
                isOtherTextRequired: type.isOtherTextRequired,
            });
    }

    public async updateAdditionalDocumentTypeAsync(type: AdditionalDocumentType): Promise<void> {
        await this.putAsync(`v1/type/client/additionalDocument/${type.id}`, type);
    }

    public async archiveAdditionalDocumentTypeAsync(type: AdditionalDocumentType): Promise<void> {
        await this.deleteAsync(`v1/type/client/additionalDocument/${type.id}`);
    }

    public async getAppointmentCancellationTypesAsync(): Promise<Array<AppointmentCancellationType>> {
        return await this.getAsync<Array<AppointmentCancellationType>>(`v1/type/appointment/cancellation`);
    }

    public async createAppointmentCancellationTypeAsync(type: AppointmentCancellationType): Promise<void> {
        await this.postAsync(`v1/type/appointment/cancellation`,
            {
                value: type.value,
                isNoLongerInterested: type.isNoLongerInterested,
                isAdditionalCommitments: type.isAdditionalCommitments,
                isSickness: type.isSickness,
            });
    }

    public async updateAppointmentCancellationTypeAsync(type: AppointmentCancellationType): Promise<void> {
        await this.putAsync(`v1/type/appointment/cancellation/${type.id}`, type);
    }

    public async archiveAppointmentCancellationTypeAsync(type: AppointmentCancellationType): Promise<void> {
        await this.deleteAsync(`v1/type/appointment/cancellation/${type.id}`);
    }

    public async getAppointmentExecutorTypesAsync(): Promise<Array<AppointmentExecutorType>> {
        return await this.getAsync<Array<AppointmentExecutorType>>(`v1/type/appointment/executor`);
    }

    public async createAppointmentExecutorTypeAsync(type: AppointmentExecutorType): Promise<void> {
        await this.postAsync(`v1/type/appointment/executor`,
            {
                value: type.value,
                isProfessionalExecutor: type.isProfessionalExecutor,
            });
    }

    public async updateAppointmentExecutorTypeAsync(type: AppointmentExecutorType): Promise<void> {
        await this.putAsync(`v1/type/appointment/executor/${type.id}`, type);
    }

    public async archiveAppointmentExecutorTypeAsync(type: AppointmentExecutorType): Promise<void> {
        await this.deleteAsync(`v1/type/appointment/executor/${type.id}`);
    }

    public async getAppointmentReasonTypesAsync(): Promise<Array<AppointmentReasonType>> {
        return await this.getAsync<Array<AppointmentReasonType>>(`v1/type/appointment/reason`);
    }

    public async createAppointmentReasonTypeAsync(type: AppointmentReasonType): Promise<void> {
        await this.postAsync(`v1/type/appointment/reason`, { value: type.value });
    }

    public async updateAppointmentReasonTypeAsync(type: AppointmentReasonType): Promise<void> {
        await this.putAsync(`v1/type/appointment/reason/${type.id}`, type);
    }

    public async archiveAppointmentReasonTypeAsync(type: AppointmentReasonType): Promise<void> {
        await this.deleteAsync(`v1/type/appointment/reason/${type.id}`);
    }

    public async getAppointmentNoInstructionTypesAsync(): Promise<Array<AppointmentNoInstructionType>> {
        return await this.getAsync<Array<AppointmentNoInstructionType>>(`v1/type/appointment/noInstruction`);
    }

    public async createAppointmentNoInstructionTypeAsync(type: AppointmentNoInstructionType): Promise<void> {
        await this.postAsync(`v1/type/appointment/noInstruction`, { value: type.value });
    }

    public async updateAppointmentNoInstructionTypeAsync(type: AppointmentNoInstructionType): Promise<void> {
        await this.putAsync(`v1/type/appointment/noInstruction/${type.id}`, type);
    }

    public async archiveAppointmentNoInstructionTypeAsync(type: AppointmentNoInstructionType): Promise<void> {
        await this.deleteAsync(`v1/type/appointment/noInstruction/${type.id}`);
    }

    public async getAppointmentOutcomeTypesAsync(): Promise<Array<AppointmentOutcomeType>> {
        return await this.getAsync<Array<AppointmentOutcomeType>>(`v1/type/appointment/outcome`);
    }

    public async createAppointmentOutcomeTypeAsync(type: AppointmentOutcomeType): Promise<void> {
        await this.postAsync(`v1/type/appointment/outcome`,
            {
                value: type.value,
                isSat: type.isSat,
                isNotSat: type.isNotSat,
                isCancelled: type.isCancelled,
            });
    }

    public async updateAppointmentOutcomeTypeAsync(type: AppointmentOutcomeType): Promise<void> {
        await this.putAsync(`v1/type/appointment/outcome/${type.id}`, type);
    }

    public async archiveAppointmentOutcomeTypeAsync(type: AppointmentOutcomeType): Promise<void> {
        await this.deleteAsync(`v1/type/appointment/outcome/${type.id}`);
    }

    public async getAppointmentPaymentStatusTypesAsync(): Promise<Array<AppointmentPaymentStatusType>> {
        return await this.getAsync<Array<AppointmentPaymentStatusType>>(`v1/type/appointment/paymentStatus`);
    }

    public async createAppointmentPaymentStatusTypeAsync(type: AppointmentPaymentStatusType): Promise<void> {
        await this.postAsync(`v1/type/appointment/paymentStatus`,
            {
                value: type.value,
                isSigningPending: type.isSigningPending,
                isSigningBooked: type.isSigningBooked,
                isBanked: type.isBanked,
                isCleared: type.isCleared,
                isPartPayment: type.isPartPayment,
                isDebtor: type.isDebtor,
                isNoLongerProceeding: type.isNoLongerProceeding,
                isWrittenOff: type.isWrittenOff,
                isComplete: type.isComplete,
                isAdminOnly: type.isAdminOnly,
            });
    }

    public async updateAppointmentPaymentStatusTypeAsync(type: AppointmentPaymentStatusType): Promise<void> {
        await this.putAsync(`v1/type/appointment/paymentStatus/${type.id}`, type);
    }

    public async archiveAppointmentPaymentStatusTypeAsync(type: AppointmentPaymentStatusType): Promise<void> {
        await this.deleteAsync(`v1/type/appointment/paymentStatus/${type.id}`);
    }

    public async getAppointmentProductTypesAsync(): Promise<Array<AppointmentProductType>> {
        return await this.getAsync<Array<AppointmentProductType>>(`v1/type/appointment/product`);
    }

    public async createAppointmentProductTypeAsync(type: AppointmentProductType): Promise<void> {
        await this.postAsync(`v1/type/appointment/product`,
            {
                value: type.value,
                isWill: type.isWill,
                isLpa: type.isLpa,
            });
    }

    public async updateAppointmentProductTypeAsync(type: AppointmentProductType): Promise<void> {
        await this.putAsync(`v1/type/appointment/product/${type.id}`, type);
    }

    public async archiveAppointmentProductTypeAsync(type: AppointmentProductType): Promise<void> {
        await this.deleteAsync(`v1/type/appointment/product/${type.id}`);
    }

    public async getAppointmentTypesAsync(): Promise<Array<AppointmentType>> {
        return await this.getAsync<Array<AppointmentType>>(`v1/type/appointment`);
    }

    public async createAppointmentTypeAsync(type: AppointmentType): Promise<void> {
        await this.postAsync(`v1/type/appointment`,
            {
                value: type.value,
                isLocationRequired: type.isLocationRequired,
                isInitialAppointment: type.isInitialAppointment,
                isSigningAppointment: type.isSigningAppointment,
            });
    }

    public async updateAppointmentTypeAsync(type: AppointmentType): Promise<void> {
        await this.putAsync(`v1/type/appointment/${type.id}`, type);
    }

    public async archiveAppointmentTypeAsync(type: AppointmentType): Promise<void> {
        await this.deleteAsync(`v1/type/appointment/${type.id}`);
    }

    public async getClientOriginalSourceTypesAsync(): Promise<Array<ClientOriginalSourceType>> {
        return await this.getAsync<Array<ClientOriginalSourceType>>(`v1/type/client/originalSource`);
    }

    public async createClientOriginalSourceTypeAsync(type: ClientOriginalSourceType): Promise<void> {
        await this.postAsync(`v1/type/client/originalSource`, {
            value: type.value,
            isValidForAcquisition: type.isValidForAcquisition,
        });
    }

    public async updateClientOriginalSourceTypeAsync(type: ClientOriginalSourceType): Promise<void> {
        await this.putAsync(`v1/type/client/originalSource/${type.id}`, type);
    }

    public async archiveClientOriginalSourceTypeAsync(type: ClientOriginalSourceType): Promise<void> {
        await this.deleteAsync(`v1/type/client/originalSource/${type.id}`);
    }

    public async getClientRelationshipTypesAsync(): Promise<Array<ClientRelationshipType>> {
        return await this.getAsync<Array<ClientRelationshipType>>(`v1/type/client/relationship`);
    }

    public async createClientRelationshipTypeAsync(type: ClientRelationshipType): Promise<void> {
        await this.postAsync(`v1/type/client/relationship`, { value: type.value });
    }

    public async updateClientRelationshipTypeAsync(type: ClientRelationshipType): Promise<void> {
        await this.putAsync(`v1/type/client/relationship/${type.id}`, type);
    }

    public async archiveClientRelationshipTypeAsync(type: ClientRelationshipType): Promise<void> {
        await this.deleteAsync(`v1/type/client/relationship/${type.id}`);
    }

    public async getClientStatusTypesAsync(): Promise<Array<ClientStatusType>> {
        return await this.getAsync<Array<ClientStatusType>>(`v1/type/client/status`);
    }

    public async createClientStatusTypeAsync(type: ClientStatusType): Promise<void> {
        await this.postAsync(`v1/type/client/status`,
            {
                value: type.value,
                isClient: type.isClient,
                isLead: type.isLead,
                isBusinessWillClient: type.isBusinessWillClient,
                isDeceased: type.isDeceased,
                isAccountArchived: type.isAccountArchived,
            });
    }

    public async updateClientStatusTypeAsync(type: ClientStatusType): Promise<void> {
        await this.putAsync(`v1/type/client/status/${type.id}`, type);
    }

    public async archiveClientStatusTypeAsync(type: ClientStatusType): Promise<void> {
        await this.deleteAsync(`v1/type/client/status/${type.id}`);
    }

    public async getClientTitleTypesAsync(): Promise<Array<ClientTitleType>> {
        return await this.getAsync<Array<ClientTitleType>>(`v1/type/client/title`);
    }

    public async createClientTitleTypeAsync(type: ClientTitleType): Promise<void> {
        await this.postAsync(`v1/type/client/title`, { value: type.value });
    }

    public async updateClientTitleTypeAsync(type: ClientTitleType): Promise<void> {
        await this.putAsync(`v1/type/client/title/${type.id}`, type);
    }

    public async archiveClientTitleTypeAsync(type: ClientTitleType): Promise<void> {
        await this.deleteAsync(`v1/type/client/title/${type.id}`);
    }

    public async getInteractionLetterTypesAsync(): Promise<Array<InteractionLetterType>> {
        return await this.getAsync<Array<InteractionLetterType>>(`v1/type/interaction/letter`);
    }

    public async createInteractionLetterTypeAsync(type: InteractionLetterType): Promise<void> {
        await this.postAsync(`v1/type/interaction/letter`,
            {
                value: type.value,
                isFirstContact: type.isFirstContact,
                isSecondContact: type.isSecondContact,
                isThirdContact: type.isThirdContact,
            });
    }

    public async updateInteractionLetterTypeAsync(type: InteractionLetterType): Promise<void> {
        await this.putAsync(`v1/type/interaction/letter/${type.id}`, type);
    }

    public async archiveInteractionLetterTypeAsync(type: InteractionLetterType): Promise<void> {
        await this.deleteAsync(`v1/type/interaction/letter/${type.id}`);
    }

    public async getInteractionOutcomeTypesAsync(): Promise<Array<InteractionOutcomeType>> {
        return await this.getAsync<Array<InteractionOutcomeType>>(`v1/type/interaction/outcome`);
    }

    public async createInteractionOutcomeTypeAsync(type: InteractionOutcomeType): Promise<void> {
        await this.postAsync(`v1/type/interaction/outcome`,
            {
                value: type.value,
                isCallMadeOutcome: type.isCallMadeOutcome,
                isCallReceivedOutcome: type.isCallReceivedOutcome,
                isLFTSYOutcome: type.isLFTSYOutcome,
                isCallback: type.isCallback,
                isAppointmentBooked: type.isAppointmentBooked,
                isAppointmentRebooked: type.isAppointmentRebooked,
                isAppointmentConfirmed: type.isAppointmentConfirmed,
                isAppointmentCancelled: type.isAppointmentCancelled,
                isWillVoid: type.isWillVoid,
                isWillReleased: type.isWillReleased,
                isDeceased: type.isDeceased,
                isDoNotContact: type.isDoNotContact,
                isAllowContact: type.isAllowContact,
                isCallInTwoYears: type.isCallInTwoYears,
                isUnableToContact: type.isUnableToContact,
                isWrongNumber: type.isWrongNumber,
            });
    }

    public async updateInteractionOutcomeTypeAsync(type: InteractionOutcomeType): Promise<void> {
        await this.putAsync(`v1/type/interaction/outcome/${type.id}`, type);
    }

    public async archiveInteractionOutcomeTypeAsync(type: InteractionOutcomeType): Promise<void> {
        await this.deleteAsync(`v1/type/interaction/outcome/${type.id}`);
    }

    public async getInteractionTypesAsync(): Promise<Array<InteractionType>> {
        return await this.getAsync<Array<InteractionType>>(`v1/type/interaction`);
    }

    public async createInteractionTypeAsync(type: InteractionType): Promise<void> {
        await this.postAsync(`v1/type/interaction`,
            {
                value: type.value,
                isCallOutcomeRequired: type.isCallOutcomeRequired,
                isLetterTypeRequired: type.isLetterTypeRequired,
                isLFTSY: type.isLFTSY,
                isCallMade: type.isCallMade,
                isCallReceived: type.isCallReceived,
            });
    }

    public async updateInteractionTypeAsync(type: InteractionType): Promise<void> {
        await this.putAsync(`v1/type/interaction/${type.id}`, type);
    }

    public async archiveInteractionTypeAsync(type: InteractionType): Promise<void> {
        await this.deleteAsync(`v1/type/interaction/${type.id}`);
    }

    public async getPartnerPracticeTypesAsync(): Promise<Array<PartnerPracticeType>> {
        return await this.getAsync<Array<PartnerPracticeType>>(`v1/type/partnerpractice`);
    }

    public async getPowerOfAttorneyTypesAsync(): Promise<Array<PowerOfAttorneyType>> {
        return await this.getAsync<Array<PowerOfAttorneyType>>(`v1/type/powerOfAttorney`);
    }

    public async createPowerOfAttorneyTypeAsync(type: PowerOfAttorneyType): Promise<void> {
        await this.postAsync(`v1/type/powerOfAttorney`,
            {
                value: type.value,
                isExclusive: type.isExclusive,
                isUnknown: type.isUnknown,
            });
    }

    public async updatePowerOfAttorneyTypeAsync(type: PowerOfAttorneyType): Promise<void> {
        await this.putAsync(`v1/type/powerOfAttorney/${type.id}`, type);
    }

    public async archivePowerOfAttorneyTypeAsync(type: PowerOfAttorneyType): Promise<void> {
        await this.deleteAsync(`v1/type/powerOfAttorney/${type.id}`);
    }

    public async getWillTypesAsync(): Promise<Array<WillType>> {
        return await this.getAsync<Array<WillType>>(`v1/type/will`);
    }

    public async createWillTypeAsync(type: WillType): Promise<void> {
        await this.postAsync(`v1/type/will`, { value: type.value });
    }

    public async updateWillTypeAsync(type: WillType): Promise<void> {
        await this.putAsync(`v1/type/will/${type.id}`, type);
    }

    public async archiveWillTypeAsync(type: WillType): Promise<void> {
        await this.deleteAsync(`v1/type/will/${type.id}`);
    }

    public constructor() {
        super();
    }
}
